import { create } from 'zustand';
import type { TrackPoint, ManeuverAnalysis } from '@regatta-replay/types';

export type TrackDisplayMode = 'none' | 'sog' | 'shortTrack' | 'shortBoat' | 'fullTrack' | 'currentLeg';
export type ViewMode = 'analytical' | 'broadcast';
export type LabelMode = 'boatName' | 'sailNumber' | 'hidden';
export type TrackVariable = 'sog' | 'vmg' | 'twa' | 'heel' | 'boatSpeed';
export type TrailLength = 'none' | 'short' | 'full';
export type TrailColorMode = 'boat' | 'sog';

export interface TrailSettings {
  length: TrailLength;
  colorMode: TrailColorMode;
  /** #1045: short-tail duration in minutes (1/3/5/10); undefined = 10. */
  shortTailMinutes?: number;
}

export const SHORT_TAIL_DEFAULT_MINUTES = 10;
/** #1045: the ⚙️ Trails duration ladder — keep the default on the ladder. */
export const SHORT_TAIL_MINUTE_OPTIONS = [1, 3, 5, 10] as const;

export function shortTailWindowMs(settings: TrailSettings): number {
  return (settings.shortTailMinutes ?? SHORT_TAIL_DEFAULT_MINUTES) * 60_000;
}

export const DEFAULT_TRAIL_SETTINGS: TrailSettings = {
  length: 'short',
  colorMode: 'sog',
  shortTailMinutes: SHORT_TAIL_DEFAULT_MINUTES,
};

export function trailSettingsToTrackDisplayMode(settings: TrailSettings): TrackDisplayMode {
  if (settings.length === 'none') return 'none';
  if (settings.length === 'full') {
    return settings.colorMode === 'sog' ? 'sog' : 'fullTrack';
  }
  return settings.colorMode === 'sog' ? 'shortTrack' : 'shortBoat';
}

export function trackDisplayModeToTrailSettings(mode: TrackDisplayMode): TrailSettings {
  switch (mode) {
    case 'none':
      return { length: 'none', colorMode: 'sog' };
    case 'sog':
      return { length: 'full', colorMode: 'sog' };
    case 'fullTrack':
      return { length: 'full', colorMode: 'boat' };
    case 'shortBoat':
      return { length: 'short', colorMode: 'boat' };
    case 'shortTrack':
    case 'currentLeg':
    default:
      return { length: 'short', colorMode: 'sog' };
  }
}

export const PLAYBACK_BASEMAP_STORAGE_KEY = 'regatta-viewer-basemap';

/** Jason memo 243: YouTube-style skip — 10 s of race time per press, scaled
 *  by the current playback speed so a skip stays meaningful at 40x. */
export const SKIP_BASE_SECONDS = 10;

/** The speed ladder — one source of truth for the transport's cycling
 *  button, the ↑/↓ hotkeys, and anything else that steps speed (#1285). */
export const SPEED_OPTIONS: readonly number[] = [1, 2, 5, 10, 20, 40, 100];

/** Jason memo 244: the ladder-rung ladder — "we don't need a 500 meter
 *  ladder... 200, 100, 50 and 25 meters". Keep Off (null) separate. */
export const LADDER_RUNG_SPACING_OPTIONS = [25, 50, 100, 200] as const;

/** A stale off-ladder spacing (e.g. the removed 500 m, or a saved pref from
 *  an older build) snaps to the closest remaining rung instead of rendering
 *  an option the UI no longer offers. */
export function normalizeLadderRungSpacing(spacing: number | null): number | null {
  if (spacing === null) return null;
  return LADDER_RUNG_SPACING_OPTIONS.reduce((best, option) =>
    Math.abs(option - spacing) < Math.abs(best - spacing) ? option : best,
  );
}

export interface HoveredTrackPoint {
  boatId: string;
  lat: number;
  lon: number;
  time: string;
  sog: number;
  cog: number;
}

interface PlaybackState {
  isPlaying: boolean;
  speed: number;
  currentTime: string;
  startTime: string;
  endTime: string;
  mapBearing: number | null;
  trackDisplayMode: TrackDisplayMode;
  trailSettings: TrailSettings;
  viewMode: ViewMode;
  labelMode: LabelMode;
  isFullScreen: boolean;
  soundEnabled: boolean;
  timeBracket: { start: string; end: string } | null;
  selectedVariable: TrackVariable;
  ladderRungSpacing: number | null;
  showManeuverMarkers: boolean;
  showLaylines: boolean;
  showSatellite: boolean;
  // #1027: viewer map overlays are opt-in — the map itself is the product.
  showCourseLegend: boolean;
  showSpeedScale: boolean;
  windDirection: number | null;  // User-set wind direction (from compass or race data)
  hoveredTrackPoint: HoveredTrackPoint | null;
  hoverTime: string | null;  // ISO 8601 — from graph hover, independent of playback
  selectedManeuver: { boatId: string; maneuver: ManeuverAnalysis } | null;
  analyticsPanelOpen: boolean;

  // Playback engine internals
  _rafId: number | null;
  /** Last RAF timestamp, retained for diagnostics and deterministic tests. */
  _lastFrameTime: number | null;
  /** Monotonic wall-clock anchor used to derive race time without drift. */
  _wallClockAnchorMs: number | null;
  /** Race-time anchor paired with `_wallClockAnchorMs`. */
  _raceClockAnchorMs: number | null;

  // Actions
  play: () => void;
  pause: () => void;
  seek: (time: string) => void;
  /** Jason memo 243: relative seek, clamped to [startTime, endTime]. */
  skipBy: (deltaMs: number) => void;
  setSpeed: (speed: number) => void;
  /** #1285 ↑/↓ hotkeys: step the SPEED_OPTIONS ladder without wrapping —
   *  ↑ from 100x stays 100x, ↓ from 1x stays 1x. An off-ladder speed
   *  (broadcast's 20x-adjacent presets, legacy 30x) steps to the adjacent
   *  rung in the pressed direction. */
  stepSpeed: (direction: 1 | -1) => void;
  /** #1028 cycling speed button, shared by the replay transport and the 360
   *  transport bar (#1444): next SPEED_OPTIONS rung, wrapping past the top.
   *  An off-ladder speed (e.g. broadcast's 30x) steps to the next-faster
   *  rung instead of snapping back to 1x. */
  cycleSpeed: () => void;
  initialize: (startTime: string, endTime: string) => void;
  /** Update the playback bounds WITHOUT resetting the scrubber — currentTime
   *  is clamped into the new range (editor End/Start edits, #Bir 2026-07-08). */
  setRange: (startTime: string, endTime: string) => void;
  setMapBearing: (bearing: number | null) => void;
  setTrackDisplayMode: (mode: TrackDisplayMode) => void;
  setTrailSettings: (settings: TrailSettings) => void;
  setViewMode: (mode: ViewMode) => void;
  setLabelMode: (mode: LabelMode) => void;
  toggleFullScreen: () => void;
  toggleSound: () => void;
  setLadderRungSpacing: (spacing: number | null) => void;
  setTimeBracket: (bracket: { start: string; end: string } | null) => void;
  setSelectedVariable: (variable: TrackVariable) => void;
  setShowManeuverMarkers: (show: boolean) => void;
  setShowLaylines: (show: boolean) => void;
  setShowCourseLegend: (show: boolean) => void;
  setShowSpeedScale: (show: boolean) => void;
  /** #1044: explicit basemap pick — persists even when re-picking the current one. */
  setShowSatellite: (show: boolean) => void;
  toggleSatellite: () => void;
  setWindDirection: (dir: number | null) => void;
  setHoveredTrackPoint: (point: HoveredTrackPoint | null) => void;
  setHoverTime: (time: string | null) => void;
  setSelectedManeuver: (m: { boatId: string; maneuver: ManeuverAnalysis } | null) => void;
  setAnalyticsPanelOpen: (open: boolean) => void;
  dispose: () => void;
}

function readPersistedShowSatellite(): boolean {
  try {
    return globalThis.localStorage?.getItem(PLAYBACK_BASEMAP_STORAGE_KEY) === 'satellite';
  } catch {
    return false;
  }
}

/** #1044: whether the user has ever chosen a basemap (viewer defaults to
 *  satellite only when they haven't — an explicit choice always wins). */
export function hasPersistedBasemapPreference(): boolean {
  try {
    return globalThis.localStorage?.getItem(PLAYBACK_BASEMAP_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

function persistShowSatellite(showSatellite: boolean): void {
  try {
    globalThis.localStorage?.setItem(
      PLAYBACK_BASEMAP_STORAGE_KEY,
      showSatellite ? 'satellite' : 'outdoors',
    );
  } catch {
    // Storage can be unavailable in private browsing or tests; the toggle still works in memory.
  }
}

type PlaybackSet = (fn: (state: PlaybackState) => Partial<PlaybackState>) => void;
type PlaybackGet = () => PlaybackState;

let removePlaybackVisibilityListener: (() => void) | null = null;

function monotonicNow(): number {
  return typeof globalThis.performance?.now === 'function'
    ? globalThis.performance.now()
    : Date.now();
}

function actionTime(state: PlaybackState): number {
  return Math.max(monotonicNow(), state._lastFrameTime ?? Number.NEGATIVE_INFINITY);
}

function parseTime(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampRaceTime(state: PlaybackState, raceMs: number): number {
  const startMs = parseTime(state.startTime);
  const endMs = parseTime(state.endTime);
  return Math.max(
    startMs ?? Number.NEGATIVE_INFINITY,
    Math.min(raceMs, endMs ?? Number.POSITIVE_INFINITY),
  );
}

function deriveAnchoredRaceTime(state: PlaybackState, now: number): number | null {
  const currentMs = parseTime(state.currentTime);
  if (currentMs === null) return null;
  if (state._wallClockAnchorMs === null || state._raceClockAnchorMs === null) {
    return clampRaceTime(state, currentMs);
  }

  const elapsedWallMs = Math.max(0, now - state._wallClockAnchorMs);
  return clampRaceTime(state, state._raceClockAnchorMs + elapsedWallMs * state.speed);
}

function clearPlaybackVisibilityListener(): void {
  removePlaybackVisibilityListener?.();
  removePlaybackVisibilityListener = null;
}

function isDocumentHidden(): boolean {
  return typeof globalThis.document !== 'undefined'
    && globalThis.document.visibilityState === 'hidden';
}

function bindPlaybackVisibilityListener(set: PlaybackSet, get: PlaybackGet): void {
  clearPlaybackVisibilityListener();
  if (typeof globalThis.document?.addEventListener !== 'function') return;

  const handleVisibilityChange = () => {
    const state = get();
    if (!state.isPlaying) return;

    if (isDocumentHidden()) {
      const now = actionTime(state);
      const raceMs = deriveAnchoredRaceTime(state, now);
      if (state._rafId !== null) cancelAnimationFrame(state._rafId);
      set(() => ({
        currentTime: raceMs === null ? state.currentTime : new Date(raceMs).toISOString(),
        _rafId: null,
        _lastFrameTime: null,
        _wallClockAnchorMs: null,
        _raceClockAnchorMs: null,
      }));
      return;
    }

    const resumed = get();
    if (!resumed.isPlaying || resumed._rafId !== null) return;
    const now = monotonicNow();
    const raceMs = parseTime(resumed.currentTime);
    if (raceMs === null) return;
    set(() => ({
      _lastFrameTime: now,
      _wallClockAnchorMs: now,
      _raceClockAnchorMs: raceMs,
    }));
    startPlaybackLoop(set, get);
  };

  globalThis.document.addEventListener('visibilitychange', handleVisibilityChange);
  removePlaybackVisibilityListener = () => {
    globalThis.document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

function startPlaybackLoop(set: PlaybackSet, get: PlaybackGet) {
  const tick = (now: number) => {
    const state = get();
    if (!state.isPlaying) return;

    // Browsers suspend RAF in background tabs. Freeze race time here as a
    // defensive fallback if visibilitychange has not run yet; the visibility
    // listener re-anchors and restarts the loop on return.
    if (isDocumentHidden()) {
      const raceMs = deriveAnchoredRaceTime(state, now);
      set(() => ({
        currentTime: raceMs === null ? state.currentTime : new Date(raceMs).toISOString(),
        _rafId: null,
        _lastFrameTime: null,
        _wallClockAnchorMs: null,
        _raceClockAnchorMs: null,
      }));
      return;
    }

    const currentMs = parseTime(state.currentTime);
    const endMs = parseTime(state.endTime);
    if (currentMs === null || endMs === null) return;

    if (state._wallClockAnchorMs === null || state._raceClockAnchorMs === null) {
      const rafId = requestAnimationFrame(tick);
      set(() => ({
        _rafId: rafId,
        _lastFrameTime: now,
        _wallClockAnchorMs: now,
        _raceClockAnchorMs: currentMs,
      }));
      return;
    }

    const newMs = deriveAnchoredRaceTime(state, now) ?? currentMs;

    if (newMs >= endMs) {
      clearPlaybackVisibilityListener();
      set(() => ({
        currentTime: state.endTime,
        isPlaying: false,
        _rafId: null,
        _lastFrameTime: null,
        _wallClockAnchorMs: null,
        _raceClockAnchorMs: null,
      }));
      return;
    }

    const rafId = requestAnimationFrame(tick);
    set(() => ({
      currentTime: new Date(newMs).toISOString(),
      _lastFrameTime: now,
      _rafId: rafId,
    }));
  };

  const rafId = requestAnimationFrame(tick);
  set(() => ({ _rafId: rafId }));
}

export const usePlaybackStore = create<PlaybackState>()((set, get) => ({
  isPlaying: false,
  // Jason 2026-07-04: races should move on open — 40x default (was 20x).
  speed: 40,
  currentTime: '',
  startTime: '',
  endTime: '',
  mapBearing: null,
  trackDisplayMode: trailSettingsToTrackDisplayMode(DEFAULT_TRAIL_SETTINGS),
  trailSettings: DEFAULT_TRAIL_SETTINGS,
  viewMode: 'analytical',
  labelMode: 'sailNumber',
  isFullScreen: false,
  soundEnabled: true,
  timeBracket: null,
  selectedVariable: 'sog',
  ladderRungSpacing: null,
  showManeuverMarkers: true,
  showLaylines: false,
  showCourseLegend: false,
  showSpeedScale: false,
  showSatellite: readPersistedShowSatellite(),
  windDirection: null,
  hoveredTrackPoint: null,
  hoverTime: null,
  selectedManeuver: null,
  analyticsPanelOpen: true,
  _rafId: null,
  _lastFrameTime: null,
  _wallClockAnchorMs: null,
  _raceClockAnchorMs: null,

  play: () => {
    const state = get();
    if (state.isPlaying) return;

    const currentTime = state.currentTime === state.endTime && state.endTime
      ? state.startTime
      : state.currentTime;
    const raceMs = parseTime(currentTime);
    if (raceMs === null) return;
    const now = monotonicNow();
    set(() => ({
      currentTime,
      isPlaying: true,
      _lastFrameTime: now,
      _wallClockAnchorMs: now,
      _raceClockAnchorMs: raceMs,
    }));
    bindPlaybackVisibilityListener(set, get);
    if (!isDocumentHidden()) startPlaybackLoop(set, get);
  },

  pause: () => {
    const state = get();
    // Pausing an already-paused, anchor-less store is a pure no-op (#1371):
    // the old unconditional write re-derived currentTime through
    // clampRaceTime, silently yanking an out-of-range LINKED clock back to
    // the range edge — flapping against the raw video times the sync seeks
    // write (the video owns the replay clock while attached; the
    // public-race-viewer e2e pins that contract).
    if (!state.isPlaying && state._wallClockAnchorMs === null && state._rafId === null) {
      return;
    }
    const now = actionTime(state);
    const raceMs = deriveAnchoredRaceTime(state, now);
    if (state._rafId !== null) cancelAnimationFrame(state._rafId);
    clearPlaybackVisibilityListener();
    set(() => ({
      currentTime: raceMs === null ? state.currentTime : new Date(raceMs).toISOString(),
      isPlaying: false,
      _rafId: null,
      _lastFrameTime: null,
      _wallClockAnchorMs: null,
      _raceClockAnchorMs: null,
    }));
  },

  seek: (time) => {
    // No range clamp here — in LINKED mode the video OWNS the replay clock,
    // including moments outside the replay window (public-race-viewer e2e
    // contract; real races carry clips from before the window). The #1371
    // storm is instead stopped by the equality no-op below (identical
    // target ⇒ no set(), zero subscriber notifications), the ≤10 Hz sender
    // pacing in VideoClipPlayer, and pause()'s paused-branch no-op above.
    const state = get();
    if (!state.isPlaying) {
      if (state.currentTime !== time) set(() => ({ currentTime: time }));
      return;
    }
    const raceMs = parseTime(time);
    if (raceMs === null) {
      if (state.currentTime !== time) set(() => ({ currentTime: time }));
      return;
    }
    const now = actionTime(state);
    set(() => ({
      currentTime: time,
      _lastFrameTime: now,
      _wallClockAnchorMs: now,
      _raceClockAnchorMs: raceMs,
    }));
  },

  skipBy: (deltaMs) => set((state) => {
    if (!state.currentTime || !state.startTime || !state.endTime) return {};
    const startMs = new Date(state.startTime).getTime();
    const endMs = new Date(state.endTime).getTime();
    // While playing, the running clock is anchor-driven: a currentTime write
    // without re-anchoring is overwritten by the next frame's derivation from
    // the OLD anchor — the skip visibly "did nothing" (Bir 2026-07-15). Skip
    // from the anchored instant and re-anchor, mirroring setSpeed/seek.
    const now = actionTime(state);
    const baseMs = state.isPlaying
      ? deriveAnchoredRaceTime(state, now) ?? new Date(state.currentTime).getTime()
      : new Date(state.currentTime).getTime();
    const nextMs = Math.min(endMs, Math.max(startMs, baseMs + deltaMs));
    if (!state.isPlaying) return { currentTime: new Date(nextMs).toISOString() };
    return {
      currentTime: new Date(nextMs).toISOString(),
      _lastFrameTime: now,
      _wallClockAnchorMs: now,
      _raceClockAnchorMs: nextMs,
    };
  }),

  setSpeed: (speed) => set((state) => {
    if (!state.isPlaying) return { speed };
    const now = actionTime(state);
    const raceMs = deriveAnchoredRaceTime(state, now);
    if (raceMs === null) return { speed };
    return {
      speed,
      currentTime: new Date(raceMs).toISOString(),
      _lastFrameTime: now,
      _wallClockAnchorMs: now,
      _raceClockAnchorMs: raceMs,
    };
  }),

  stepSpeed: (direction) => {
    const { speed, setSpeed } = get();
    const ladder = SPEED_OPTIONS;
    const index = ladder.indexOf(speed);
    let next: number;
    if (index >= 0) {
      // No wrap: clamp to the ladder ends.
      next = ladder[Math.min(ladder.length - 1, Math.max(0, index + direction))];
    } else {
      next =
        direction > 0
          ? (ladder.find((option) => option > speed) ?? ladder[ladder.length - 1])
          : ([...ladder].reverse().find((option) => option < speed) ?? ladder[0]);
    }
    // setSpeed owns the mid-playback re-anchor (currentTime derivation).
    if (next !== speed) setSpeed(next);
  },

  cycleSpeed: () => {
    const { speed, setSpeed } = get();
    const index = SPEED_OPTIONS.indexOf(speed);
    const next =
      index >= 0
        ? SPEED_OPTIONS[(index + 1) % SPEED_OPTIONS.length]
        : (SPEED_OPTIONS.find((option) => option > speed) ?? SPEED_OPTIONS[0]);
    // setSpeed owns the mid-playback re-anchor (currentTime derivation).
    if (next !== speed) setSpeed(next);
  },

  initialize: (startTime, endTime) => {
    const { _rafId } = get();
    if (_rafId !== null) cancelAnimationFrame(_rafId);
    clearPlaybackVisibilityListener();
    set(() => ({
      startTime,
      endTime,
      currentTime: startTime,
      isPlaying: false,
      hoveredTrackPoint: null,
      hoverTime: null,
      _rafId: null,
      _lastFrameTime: null,
      _wallClockAnchorMs: null,
      _raceClockAnchorMs: null,
    }));
  },

  setRange: (startTime, endTime) => set((state) => {
    const now = actionTime(state);
    const anchoredMs = state.isPlaying ? deriveAnchoredRaceTime(state, now) : null;
    const currentTime = anchoredMs === null
      ? state.currentTime
      : new Date(anchoredMs).toISOString();
    const clampedTime = !currentTime || currentTime < startTime
      ? startTime
      : currentTime > endTime
        ? endTime
        : currentTime;
    const clampedMs = parseTime(clampedTime);
    return {
      startTime,
      endTime,
      currentTime: clampedTime,
      ...(state.isPlaying && clampedMs !== null
        ? {
            _lastFrameTime: now,
            _wallClockAnchorMs: now,
            _raceClockAnchorMs: clampedMs,
          }
        : {}),
    };
  }),

  setMapBearing: (bearing) => set(() => ({ mapBearing: bearing })),
  setTrackDisplayMode: (mode) =>
    set((state) => ({
      trackDisplayMode: mode,
      // Preserve the picked short-tail duration across mode round-trips (#1045).
      trailSettings: {
        ...trackDisplayModeToTrailSettings(mode),
        shortTailMinutes: state.trailSettings.shortTailMinutes,
      },
    })),
  setTrailSettings: (settings) =>
    set((state) => ({
      // Style pickers pass bare {length, colorMode} — the picked short-tail
      // duration survives unless the caller sets one explicitly (#1045).
      trailSettings: {
        shortTailMinutes: state.trailSettings.shortTailMinutes,
        ...settings,
      },
      trackDisplayMode: trailSettingsToTrackDisplayMode(settings),
    })),
  setViewMode: (mode) => set(() => ({ viewMode: mode })),
  setLabelMode: (mode) => set(() => ({ labelMode: mode })),
  toggleFullScreen: () => set((s) => ({ isFullScreen: !s.isFullScreen })),
  toggleSound: () => set((s) => ({ soundEnabled: !s.soundEnabled })),
  setLadderRungSpacing: (spacing) =>
    set(() => ({ ladderRungSpacing: normalizeLadderRungSpacing(spacing) })),
  setTimeBracket: (bracket) => set(() => ({ timeBracket: bracket })),
  setSelectedVariable: (variable) => set(() => ({ selectedVariable: variable })),
  setShowManeuverMarkers: (show) => set(() => ({ showManeuverMarkers: show })),
  setShowLaylines: (show) => set(() => ({ showLaylines: show })),
  setShowCourseLegend: (show) => set(() => ({ showCourseLegend: show })),
  setShowSpeedScale: (show) => set(() => ({ showSpeedScale: show })),
  setShowSatellite: (show) => set(() => {
    persistShowSatellite(show);
    return { showSatellite: show };
  }),
  toggleSatellite: () => set((s) => {
    const showSatellite = !s.showSatellite;
    persistShowSatellite(showSatellite);
    return { showSatellite };
  }),
  setWindDirection: (dir) => set(() => ({ windDirection: dir })),
  setHoveredTrackPoint: (point) => set(() => ({ hoveredTrackPoint: point })),
  setHoverTime: (time) => set(() => ({ hoverTime: time })),
  setSelectedManeuver: (m) => set(() => ({ selectedManeuver: m })),
  setAnalyticsPanelOpen: (open) => set(() => ({ analyticsPanelOpen: open })),

  dispose: () => {
    const { _rafId } = get();
    if (_rafId !== null) cancelAnimationFrame(_rafId);
    clearPlaybackVisibilityListener();
    set(() => ({
      isPlaying: false,
      _rafId: null,
      _lastFrameTime: null,
      _wallClockAnchorMs: null,
      _raceClockAnchorMs: null,
    }));
  },
}));

// ── Interpolation Utilities ──

export function interpolateBoatPosition(
  points: TrackPoint[],
  currentTime: string
): { lat: number; lon: number; heading: number; sog: number; cog: number; twd?: number; heel?: number; pitch?: number; boatSpeed?: number; depth?: number; waterTemp?: number } | null {
  if (points.length === 0) return null;

  const targetMs = new Date(currentTime).getTime();
  const firstMs = new Date(points[0].timestamp).getTime();
  const lastMs = new Date(points[points.length - 1].timestamp).getTime();

  if (targetMs <= firstMs) {
    const p = points[0];
    return { lat: p.lat, lon: p.lon, heading: p.heading ?? p.cog ?? 0, sog: p.sog ?? 0, cog: p.cog ?? 0, twd: p.twd, heel: p.heel, pitch: p.pitch, boatSpeed: p.boatSpeed, depth: p.depth, waterTemp: p.waterTemp };
  }
  if (targetMs >= lastMs) {
    const p = points[points.length - 1];
    return { lat: p.lat, lon: p.lon, heading: p.heading ?? p.cog ?? 0, sog: p.sog ?? 0, cog: p.cog ?? 0, twd: p.twd, heel: p.heel, pitch: p.pitch, boatSpeed: p.boatSpeed, depth: p.depth, waterTemp: p.waterTemp };
  }

  // Binary search for surrounding points
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >>> 1;
    if (new Date(points[mid].timestamp).getTime() <= targetMs) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const p0 = points[lo];
  const p1 = points[hi];
  const t0 = new Date(p0.timestamp).getTime();
  const t1 = new Date(p1.timestamp).getTime();
  const t = t1 === t0 ? 0 : (targetMs - t0) / (t1 - t0);

  // Use nearest point's TWD (stable over short intervals, not worth interpolating angle)
  const twd = (t < 0.5 ? p0.twd : p1.twd) ?? p0.twd ?? p1.twd;

  // Use nearest point for non-interpolatable sensor values
  const nearest = t < 0.5 ? p0 : p1;

  const interpolatedSog = (p0.sog ?? 0) + ((p1.sog ?? 0) - (p0.sog ?? 0)) * t;

  // V28: Use COG for marker rotation — GPS-derived, always correct.
  // Quaternion heading can be 180° off if device mounted backward (e.g. CAN 441).
  // COG = actual direction of travel. Heading = device bow direction (may be wrong).
  let heading = interpolateAngle(p0.cog ?? p0.heading ?? 0, p1.cog ?? p1.heading ?? 0, t);

  // Stabilize heading at low speeds to prevent wild flipping.
  // COG is unreliable below ~0.5 kts — find the nearest point with meaningful speed.
  const MIN_STABLE_SOG = 0.5;
  if (interpolatedSog < MIN_STABLE_SOG) {
    let stableHeading: number | null = null;
    // Search forward first (direction of travel)
    for (let i = hi; i < Math.min(hi + 60, points.length); i++) {
      if ((points[i].sog ?? 0) >= MIN_STABLE_SOG) {
        stableHeading = points[i].cog ?? points[i].heading ?? 0;
        break;
      }
    }
    // Fall back to searching backward
    if (stableHeading === null) {
      for (let i = lo; i >= Math.max(lo - 60, 0); i--) {
        if ((points[i].sog ?? 0) >= MIN_STABLE_SOG) {
          stableHeading = points[i].cog ?? points[i].heading ?? 0;
          break;
        }
      }
    }
    if (stableHeading !== null) heading = stableHeading;
  }

  return {
    lat: p0.lat + (p1.lat - p0.lat) * t,
    lon: p0.lon + (p1.lon - p0.lon) * t,
    sog: interpolatedSog,
    cog: interpolateAngle(p0.cog ?? 0, p1.cog ?? 0, t),
    heading,
    twd,
    heel: nearest.heel,
    pitch: nearest.pitch,
    boatSpeed: nearest.boatSpeed,
    depth: nearest.depth,
    waterTemp: nearest.waterTemp,
  };
}

function interpolateAngle(a: number, b: number, t: number): number {
  const diff = ((b - a + 540) % 360) - 180;
  return ((a + diff * t) + 360) % 360;
}

export function getTrackUpToTime(points: TrackPoint[], currentTime: string): TrackPoint[] {
  const targetMs = new Date(currentTime).getTime();
  let endIdx = points.length;
  for (let i = 0; i < points.length; i++) {
    if (new Date(points[i].timestamp).getTime() > targetMs) {
      endIdx = i;
      break;
    }
  }
  return points.slice(0, endIdx);
}

export function getShortTrack(
  points: TrackPoint[],
  currentTime: string,
  durationMs: number = 600000
): TrackPoint[] {
  const targetMs = new Date(currentTime).getTime();
  const cutoffMs = targetMs - durationMs;
  const upTo = getTrackUpToTime(points, currentTime);
  const startIdx = upTo.findIndex((p) => new Date(p.timestamp).getTime() >= cutoffMs);
  return startIdx >= 0 ? upTo.slice(startIdx) : upTo;
}
